import { useLayoutEffect } from 'react';
import type { RefObject } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CustomEase } from 'gsap/CustomEase';

gsap.registerPlugin(ScrollTrigger, CustomEase);
CustomEase.create('studio-flow', '0.51, 0, 0.08, 1');
gsap.defaults({ ease: 'studio-flow', duration: 0.5 });

export function usePageMotion(root: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const element = root.current;
    if (!element) return;
    let disposed = false;
    let stopSpeech = () => {};
    const responsive = gsap.matchMedia();
    const context = gsap.context(() => {
      responsive.add(
        { desktop: '(min-width: 992px)', motion: '(prefers-reduced-motion: no-preference)' },
        (result) => {
          const { desktop, motion } = result.conditions!;
          if (!motion) return;
          const headings = element.querySelectorAll<HTMLElement>(
            desktop ? '[data-reveal]' : '[data-reveal="all"]',
          );
          headings.forEach((heading) => {
            const plain = heading.querySelectorAll('.reveal-word');
            const accents = heading.querySelectorAll('.reveal-accent');
            const timeline = gsap.timeline({
              scrollTrigger: { trigger: heading, start: 'bottom bottom', once: true },
            });
            if (plain.length)
              timeline.from(plain, {
                yPercent: 100,
                opacity: 0,
                duration: 0.75,
                stagger: { amount: 0.25 },
                ease: 'power4.inOut',
              });
            if (accents.length)
              timeline.fromTo(
                accents,
                { yPercent: 100, opacity: 0 },
                {
                  yPercent: 0,
                  opacity: 1,
                  rotation: (index) => (index % 2 ? 2 : -2),
                  duration: 1,
                  stagger: 0.25,
                  ease: 'elastic.out(1,.7)',
                },
                plain.length ? 0.375 : 0,
              );
          });
          const phrase = element.querySelector('.story-beat--last');
          if (phrase) {
            // Off desktop the page ends right under the punchline, so the beat can never reach
            // mid-screen: there the fade follows the words themselves and finishes in reach.
            const words = phrase.querySelector('.phrase');
            const timeline = gsap.timeline({
              scrollTrigger:
                desktop || !words
                  ? { trigger: phrase, start: 'bottom bottom', end: 'center center', scrub: 0.1 }
                  : { trigger: words, start: 'top 90%', end: 'bottom 60%', scrub: 0.1 },
            });
            [
              ...element.querySelectorAll('.phrase > div'),
              ...element.querySelectorAll('.reel-heading'),
            ].forEach((part) => timeline.from(part, { opacity: 0 }));
          }
          // Each message swells into focus at the centre of the screen, then yields to the next.
          element
            .querySelectorAll<HTMLElement>('.story-beat:not(.story-beat--last) > .heading')
            .forEach((heading) =>
              gsap
                .timeline({
                  scrollTrigger: {
                    trigger: heading,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 0.3,
                  },
                })
                .fromTo(
                  heading,
                  { scale: 0.82, opacity: 0.15 },
                  { scale: 1, opacity: 1, ease: 'power2.out' },
                )
                .to(heading, { scale: 0.92, opacity: 0.15, ease: 'power2.in' }),
            );
          if (!desktop) {
            element.querySelectorAll<HTMLElement>('.values-cards > .blank').forEach((card) => {
              gsap.from(card, {
                yPercent: 30,
                rotation: 0,
                opacity: 0,
                scrollTrigger: { trigger: card, start: 'top bottom', end: 'bottom 80%', scrub: 1 },
              });
            });
            return;
          }
          element.querySelectorAll<HTMLElement>('[data-float]').forEach((tag, index) =>
            gsap.to(tag, {
              rotation: 0,
              y: `${-(10 + (index % 11))}vh`,
              ease: 'power1.in',
              scrollTrigger: { trigger: tag, start: 'top bottom', end: 'bottom top', scrub: 1 },
            }),
          );
          element.querySelectorAll<HTMLElement>('[data-stagger]').forEach((group) =>
            gsap.from(group.children, {
              scale: 0.9,
              opacity: 0,
              stagger: 0.1,
              duration: 0.5,
              scrollTrigger: { trigger: group, start: '20% bottom', once: true },
            }),
          );
          const screen = element.querySelector('.reel-screen');
          if (screen) {
            gsap.fromTo(
              screen,
              { scale: 0.7, borderRadius: '4rem' },
              {
                scale: 1,
                borderRadius: 0,
                ease: 'none',
                scrollTrigger: {
                  trigger: '.reel-sticky',
                  start: 'top top',
                  end: '20% top',
                  scrub: 1,
                },
              },
            );
            gsap.to('.reel-shade', {
              opacity: 0.7,
              ease: 'none',
              scrollTrigger: {
                trigger: '.reel-sticky',
                start: 'top top',
                end: '20% top',
                scrub: 1,
              },
            });
          }
          const nav = element.querySelector('.navigation--home');
          if (nav)
            ScrollTrigger.create({
              trigger: nav,
              start: 'bottom bottom',
              onEnter: () => gsap.to('.opening-navigation', { yPercent: -100, duration: 0.5 }),
              onLeaveBack: () => gsap.to('.opening-navigation', { yPercent: 0, duration: 0.5 }),
            });
          element.querySelectorAll<HTMLElement>('.photo-pile').forEach((pile) =>
            gsap.from(pile.children, {
              x: (index) => (index ? '-10vw' : '10vw'),
              rotation: 0,
              opacity: 0,
              scrollTrigger: {
                trigger: pile,
                start: '20% bottom',
                end: 'bottom bottom',
                scrub: 1,
              },
            }),
          );
          element.querySelectorAll<HTMLElement>('.callout-visual').forEach((visual) =>
            gsap.from(visual, {
              rotation: -6,
              scrollTrigger: {
                trigger: visual,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 0.5,
              },
            }),
          );
          const values = element.querySelector('.values-cards');
          if (values)
            gsap.from(values.children, {
              x: (index) => `${[20, 10, -10, -20][index]}vw`,
              rotation: 0,
              opacity: 0,
              duration: 1.25,
              ease: 'power4.out',
              scrollTrigger: { trigger: values, start: 'top bottom', once: true },
            });
          const equation = element.querySelector('.equation');
          if (equation)
            gsap.from(equation.querySelectorAll('.tag, .icon, .blank'), {
              opacity: 0,
              y: '2rem',
              rotation: 0,
              stagger: 0.2,
              duration: 0.7,
              scrollTrigger: { trigger: equation, start: 'center bottom', once: true },
            });
          const footer = element.querySelector('.footer-scene');
          if (footer) {
            const timeline = gsap.timeline({
              scrollTrigger: {
                trigger: element.querySelector('.site-footer'),
                start: 'top 80%',
                end: 'bottom bottom',
                scrub: 1,
              },
            });
            timeline.from(
              footer.querySelectorAll('.note-position'),
              {
                y: '20vh',
                x: (index) => (index ? '-20vw' : '0vw'),
                rotation: (index) => (index ? 15 : 20),
                opacity: 0,
                duration: 0.5,
              },
              0,
            );
            timeline.from(
              footer.querySelectorAll('.footer-chip'),
              { y: '20vh', rotation: (index) => [20, -20, -60][index], opacity: 0, duration: 0.5 },
              0,
            );
            timeline.from(
              footer.querySelectorAll('.socials a'),
              { y: '20vh', x: (index) => (index ? '0vw' : '20vw'), opacity: 0, duration: 0.5 },
              0,
            );
          }
        },
      );
      // The noob answers the "one*" footnote himself, right as "guy" lands.
      const speech = element.querySelector<HTMLElement>('.speech');
      const punchline = element.querySelector('.reel-heading');
      if (speech && punchline) {
        let typing = 0;
        const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
        const show = () => {
          if (speech.classList.contains('is-shown')) return;
          speech.classList.add('is-shown');
          if (still) return;
          speech.classList.add('is-typing');
          typing = window.setTimeout(() => speech.classList.remove('is-typing'), 900);
        };
        const hide = () => {
          window.clearTimeout(typing);
          speech.classList.remove('is-shown', 'is-typing');
        };
        // Shown once the punchline passes 70% of the viewport. Read live on every scroll rather
        // than from cached trigger positions, which go stale while fonts swap in after load.
        const sync = () =>
          punchline.getBoundingClientRect().top < innerHeight * 0.7 ? show() : hide();
        addEventListener('scroll', sync, { passive: true });
        addEventListener('resize', sync);
        ScrollTrigger.addEventListener('refresh', sync);
        sync();
        // The story waits for the visitor's first scroll (skipped for reduced motion).
        const gate = () => element.classList.toggle('is-scrolled', scrollY > 8);
        if (!still) {
          element.classList.add('story-gated');
          gate();
          addEventListener('scroll', gate, { passive: true });
        }
        stopSpeech = () => {
          removeEventListener('scroll', gate);
          element.classList.remove('story-gated', 'is-scrolled');
          removeEventListener('scroll', sync);
          removeEventListener('resize', sync);
          ScrollTrigger.removeEventListener('refresh', sync);
          window.clearTimeout(typing);
        };
      }
    }, element);
    const refresh = () => {
      if (!disposed) ScrollTrigger.refresh();
    };
    void document.fonts.ready.then(refresh);
    const timer = window.setTimeout(refresh, 150);
    // Late font swaps and the model stage settling change the page height after load;
    // re-measure whenever it does so scroll positions stay true.
    let height = element.offsetHeight;
    let frame = 0;
    const settle = new ResizeObserver(() => {
      if (element.offsetHeight === height) return;
      height = element.offsetHeight;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(refresh);
    });
    settle.observe(element);
    return () => {
      disposed = true;
      window.clearTimeout(timer);
      cancelAnimationFrame(frame);
      settle.disconnect();
      stopSpeech();
      responsive.revert();
      context.revert();
    };
  }, [root]);
}
